import { Button, Card, Icon, Typography } from '@mui/material';
import { Stack } from '@mui/material';
import {
  AttendanceSessionEnum,
  JustificationBase,
  JustificationReasonSave,
  SHORT_FORMATTED_DATE_FORMAT_FNS,
  useGetAttendanceCodesQuery,
  WithAvatar,
  WithName,
} from '@schooly/api';
import { AttendanceCodeCircle } from '@schooly/components/attendance';
import { useAuth } from '@schooly/components/authentication';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import { ControlTextField } from '@schooly/components/form-text-field';
import {
  EyeCloseIcon,
  ModalConfirm,
  ModalFooter,
  ModalHeader,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { format } from 'date-fns';
import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import { JustificationContent } from '../JustificationContent';

export type JustificationChangeType = 'reopen' | 'close';

export type JustificationChangeModalData = {
  changeType: JustificationChangeType;
  codeId: string;
  relation: WithName & WithAvatar;
  date: string;
  sessions: AttendanceSessionEnum[];
  justification: JustificationBase;
  entries: Array<{
    session: AttendanceSessionEnum;
    attendance_code_id: string | null;
  }>;
};

export type JustificationChangeModalProps = {
  onSubmit: (data: JustificationReasonSave) => void;
  onClose: () => void;
} & JustificationChangeModalData;

export const JustificationChangeModal: FC<JustificationChangeModalProps> = ({
  onSubmit,
  onClose,
  changeType,
  codeId,
  relation,
  date,
  sessions,
  justification,
  entries,
}) => {
  const { formatMessage } = useIntl();
  const { schoolId = '', relationId } = useAuth();
  const formattedDate = format(newDateTimezoneOffset(date), SHORT_FORMATTED_DATE_FORMAT_FNS);
  const { data: codes } = useGetAttendanceCodesQuery(schoolId);
  const code = codes?.find((item) => item.id === codeId);
  const sessionsLabel = sessions
    .map((session) => formatMessage({ id: `attendance-${session.toUpperCase()}` }))
    .join('&');

  const form = useForm<JustificationReasonSave>({
    defaultValues: {
      text: '',
      creator_relation_id: relationId ?? '',
    },
  });
  const name = getUserFullName(relation);

  if (!relationId) return null;

  return (
    <ModalConfirm open onClose={onClose} fullWidth>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <ModalHeader
            sx={{ px: 2.5, pt: 2.5, pb: 2 }}
            multiline
            withBorderBottom={false}
            title={formatMessage(
              { id: 'attendance-justification-ConfirmMarkAttendance' },
              {
                name:
                  name || formatMessage({ id: 'attendance-justification-Student' }).toLowerCase(),
                date: formattedDate,
                session: sessionsLabel,
                codeName: code?.name ?? '',
              },
            )}
            active
          />
          <Stack gap={2} px={2.5}>
            <Typography variant="h3" color="text.primary">
              {formatMessage({
                id:
                  changeType === 'close'
                    ? 'attendance-justification-Close-Description'
                    : 'attendance-justification-Reopen-Description',
              })}
            </Typography>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              p={1}
              sx={(theme) => ({
                border: `1px solid ${theme.palette.common.main5}`,
                borderRadius: theme.spacing(1),
              })}
            >
              <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
                {name ? (
                  <>
                    <AvatarAuth user={relation} withAvatarPreview />
                    <TypographyWithOverflowHint variant="h3" noWrap>
                      {name}
                    </TypographyWithOverflowHint>
                  </>
                ) : (
                  <Typography variant="h3">{'-'}</Typography>
                )}
              </Stack>
              <Stack direction="row" gap={1} alignItems="center">
                {entries.map((entry) => (
                  <AttendanceCodeCircle
                    key={entry.session}
                    code={
                      entry.attendance_code_id
                        ? codes?.find((item) => item.id === entry.attendance_code_id)
                        : undefined
                    }
                  />
                ))}
              </Stack>
            </Stack>
            <Card sx={{ p: 1 }}>
              <JustificationContent justification={justification} />
            </Card>
            <ControlTextField
              name="text"
              control={form.control}
              label={formatMessage({
                id:
                  changeType === 'close'
                    ? 'attendance-justification-Reason'
                    : 'attendance-justification-ClarificationRequest',
              })}
              fullWidth
              multiline
              rows={4}
              rules={{ required: true }}
            />
            {changeType === 'close' && (
              <Stack direction="row" gap={1} alignItems="center">
                <Icon sx={{ color: 'common.grey' }}>
                  <EyeCloseIcon />
                </Icon>
                <Typography color="common.grey">
                  {formatMessage({ id: 'attendance-justification-VisibleToSchoolOnly' })}
                </Typography>
              </Stack>
            )}
          </Stack>
          <ModalFooter active withBorderTop={false} sx={{ px: 2.5, pt: 2, pb: 2.5, mt: 0 }}>
            <Stack gap={2} direction="row" width="100%">
              <Button variant="outlined" onClick={onClose} sx={{ flex: 1 }}>
                {formatMessage({ id: 'action-Cancel' })}
              </Button>
              <Button color="primary" type="submit" sx={{ flex: 1 }}>
                {formatMessage({ id: 'action-Confirm' })}
              </Button>
            </Stack>
          </ModalFooter>
        </form>
      </FormProvider>
    </ModalConfirm>
  );
};
